
import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import App from './App';
import './index.css';
import analytics, { hasConsent, init as initAnalytics } from './lib/analytics';
import { installSafeConsole } from './lib/safeConsole';
import { initializeOptionalSentry } from './lib/optionalSentry';

installSafeConsole();

// Environment variable validation
const requiredEnvVars = [
  'VITE_SUPABASE_URL',
  'VITE_SUPABASE_ANON_KEY',
];

const missingVars = requiredEnvVars.filter(varName => !import.meta.env[varName]);

if (missingVars.length > 0) {
  const errorMsg = `Missing required environment variables: ${missingVars.join(', ')}`;
  console.error('❌', errorMsg);
  
  if (import.meta.env.PROD) {
    // In production, show user-friendly error
    document.getElementById('root').innerHTML = `
      <div style="padding: 40px; text-align: center; font-family: system-ui;">
        <h1>Configuration Error</h1>
        <p>The application is not properly configured. Please contact support.</p>
      </div>
    `;
    throw new Error(errorMsg);
  } else {
    // In development, show detailed error
    throw new Error(errorMsg);
  }
}

// Initialize Sentry for error tracking in production
if (import.meta.env.PROD) {
  initializeOptionalSentry(import.meta.env.VITE_SENTRY_DSN, dsn => {
  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    enableLogs: true, // Enable structured logging
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
      // Automatically capture console.error calls as logs
      Sentry.consoleLoggingIntegration({ levels: ['error'] }),
    ],
    // Performance Monitoring
    tracesSampleRate: 0.1, // Capture 10% of transactions
    // Session Replay
    replaysSessionSampleRate: 0.1, // Sample 10% of sessions
    replaysOnErrorSampleRate: 1.0, // Sample 100% of sessions with errors
    // Filter out sensitive data
    beforeSend(event) {
      // Remove query strings that might contain tokens
      if (event.request?.url) {
        event.request.url = event.request.url.split('?')[0];
      }
      return event;
    },
  });
  });
}

// Register service worker for PWA
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {
      console.warn('Offline support unavailable; the application will continue.');
    });
  });
}

// Initialize GA only in production and if consent previously granted
const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_ID || import.meta.env.VITE_GA4_MEASUREMENT_ID;
if (process.env.NODE_ENV === 'production') {
  if (hasConsent() && GA_MEASUREMENT_ID) {
    initAnalytics(GA_MEASUREMENT_ID);
  }
}

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
